import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Camera, Check, ChevronLeft, ChevronRight, AlertTriangle, 
  Plus, X, Loader2, Car, Fuel, Gauge, PenTool
} from 'lucide-react';
import { toast } from 'sonner';

// Signature Pad Component
const SignaturePad = ({ onSave, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#12151a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#4ADE80';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#12151a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onClear?.();
  };

  const save = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave?.(dataUrl);
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={300}
        height={150}
        className="border border-[#1a1d24] rounded-lg w-full touch-none"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clear} className="flex-1">
          Clear
        </Button>
        <Button type="button" size="sm" onClick={save} className="flex-1 bg-[#4ADE80] text-black">
          Save Signature
        </Button>
      </div>
    </div>
  );
};

// Damage locations for the vehicle diagram
const damageLocations = [
  { id: 'front', label: 'Front', position: 'top-2 left-1/2 -translate-x-1/2' },
  { id: 'rear', label: 'Rear', position: 'bottom-2 left-1/2 -translate-x-1/2' },
  { id: 'left_side', label: 'Left Side', position: 'top-1/2 left-2 -translate-y-1/2' },
  { id: 'right_side', label: 'Right Side', position: 'top-1/2 right-2 -translate-y-1/2' },
  { id: 'roof', label: 'Roof', position: 'top-1/3 left-1/2 -translate-x-1/2' },
  { id: 'hood', label: 'Hood', position: 'top-1/4 left-1/2 -translate-x-1/2' },
  { id: 'trunk', label: 'Trunk', position: 'bottom-1/4 left-1/2 -translate-x-1/2' },
];

const damageTypes = [
  { id: 'scratch', label: 'Scratch' },
  { id: 'dent', label: 'Dent' },
  { id: 'crack', label: 'Crack' },
  { id: 'chip', label: 'Chip' },
  { id: 'rust', label: 'Rust' },
  { id: 'missing_part', label: 'Missing Part' },
  { id: 'broken', label: 'Broken' },
  { id: 'other', label: 'Other' },
];

const InspectionPage = () => {
  const { loadId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  const inspectionType = searchParams.get('type') || 'pickup';
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [load, setLoad] = useState(null);
  
  const [formData, setFormData] = useState({
    vehicle_vin: '',
    odometer_reading: '',
    fuel_level: 'half',
    condition: {
      exterior_clean: true,
      interior_clean: true,
      starts_runs: true,
      lights_work: true,
      windows_intact: true,
      tires_good: true,
      notes: '',
    },
    damages: [],
    notes: '',
    signature_data: null,
    signer_name: '',
    signer_role: inspectionType === 'pickup' ? 'shipper' : 'receiver',
  });

  const [newDamage, setNewDamage] = useState({
    location: '',
    type: '',
    description: '',
    severity: 'minor',
  });

  useEffect(() => {
    loadLoadDetails();
  }, [loadId]);

  const loadLoadDetails = async () => {
    try {
      const response = await api.get(`/loads/${loadId}`);
      setLoad(response.data);
    } catch (error) {
      toast.error('Failed to load details');
    }
  };

  const addDamage = () => {
    if (!newDamage.location || !newDamage.type || !newDamage.description) {
      toast.error('Please fill in all damage details');
      return;
    }
    setFormData({
      ...formData,
      damages: [...formData.damages, { ...newDamage }],
    });
    setNewDamage({ location: '', type: '', description: '', severity: 'minor' });
  };

  const removeDamage = (index) => {
    setFormData({
      ...formData,
      damages: formData.damages.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async () => {
    if (!formData.signature_data) {
      toast.error('Signature is required');
      return;
    }
    if (!formData.signer_name) {
      toast.error('Signer name is required');
      return;
    }

    setLoading(true);
    try {
      await api.post('/inspections', {
        load_id: loadId,
        inspection_type: inspectionType,
        vehicle_vin: formData.vehicle_vin || null,
        odometer_reading: formData.odometer_reading ? parseInt(formData.odometer_reading) : null,
        fuel_level: formData.fuel_level,
        condition: formData.condition,
        damages: formData.damages.length > 0 ? formData.damages : null,
        notes: formData.notes || null,
        signature_data: formData.signature_data,
        signer_name: formData.signer_name,
        signer_role: formData.signer_role,
      });

      toast.success(`${inspectionType === 'pickup' ? 'Pickup' : 'Delivery'} inspection completed!`);
      navigate(`/driver/load/${loadId}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit inspection';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = 4;

  return (
    <div className="min-h-screen bg-background pt-16 pb-24 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">
              {inspectionType === 'pickup' ? 'Pickup' : 'Delivery'} Inspection
            </h1>
            <p className="text-sm text-gray-400">
              {load?.pickup_location} → {load?.delivery_location}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Step {step} of {totalSteps}</span>
            <span>{Math.round((step / totalSteps) * 100)}%</span>
          </div>
          <div className="h-2 bg-[#1a1d24] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#4ADE80] transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Step 1: Vehicle Info */}
        {step === 1 && (
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="w-5 h-5 text-[#4ADE80]" />
                Vehicle Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>VIN Number (Optional)</Label>
                <Input
                  placeholder="Enter VIN"
                  value={formData.vehicle_vin}
                  onChange={(e) => setFormData({ ...formData, vehicle_vin: e.target.value })}
                  className="bg-[#0a0c10] border-[#1a1d24]"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Gauge className="w-4 h-4" />
                  Odometer Reading
                </Label>
                <Input
                  type="number"
                  placeholder="Enter mileage"
                  value={formData.odometer_reading}
                  onChange={(e) => setFormData({ ...formData, odometer_reading: e.target.value })}
                  className="bg-[#0a0c10] border-[#1a1d24]"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Fuel className="w-4 h-4" />
                  Fuel Level
                </Label>
                <Select
                  value={formData.fuel_level}
                  onValueChange={(value) => setFormData({ ...formData, fuel_level: value })}
                >
                  <SelectTrigger className="bg-[#0a0c10] border-[#1a1d24]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12151a] border-[#1a1d24]">
                    <SelectItem value="empty">Empty</SelectItem>
                    <SelectItem value="quarter">1/4 Tank</SelectItem>
                    <SelectItem value="half">1/2 Tank</SelectItem>
                    <SelectItem value="three_quarter">3/4 Tank</SelectItem>
                    <SelectItem value="full">Full</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Condition Checklist */}
        {step === 2 && (
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#4ADE80]" />
                Condition Checklist
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'exterior_clean', label: 'Exterior Clean' },
                { key: 'interior_clean', label: 'Interior Clean' },
                { key: 'starts_runs', label: 'Starts & Runs' },
                { key: 'lights_work', label: 'All Lights Work' },
                { key: 'windows_intact', label: 'Windows Intact' },
                { key: 'tires_good', label: 'Tires in Good Condition' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between p-3 bg-[#0a0c10] rounded-lg">
                  <span className="text-white">{item.label}</span>
                  <Checkbox
                    checked={formData.condition[item.key]}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      condition: { ...formData.condition, [item.key]: checked }
                    })}
                    className="data-[state=checked]:bg-[#4ADE80] data-[state=checked]:border-[#4ADE80]"
                  />
                </div>
              ))}

              <div className="space-y-2">
                <Label>Condition Notes</Label>
                <Textarea
                  placeholder="Any additional notes about vehicle condition..."
                  value={formData.condition.notes}
                  onChange={(e) => setFormData({
                    ...formData,
                    condition: { ...formData.condition, notes: e.target.value }
                  })}
                  className="bg-[#0a0c10] border-[#1a1d24]"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Damage Documentation */}
        {step === 3 && (
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#4ADE80]" />
                Damage Documentation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Existing Damages */}
              {formData.damages.length > 0 && (
                <div className="space-y-2">
                  <Label>Recorded Damages ({formData.damages.length})</Label>
                  {formData.damages.map((damage, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-[#0a0c10] rounded-lg">
                      <div>
                        <p className="text-white text-sm font-medium">
                          {damage.location.replace('_', ' ')} - {damage.type}
                        </p>
                        <p className="text-xs text-gray-500">{damage.description}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDamage(index)}
                        className="text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Damage */}
              <div className="p-4 bg-[#0a0c10] rounded-lg space-y-3">
                <Label className="text-[#4ADE80]">Add New Damage</Label>
                
                <Select
                  value={newDamage.location}
                  onValueChange={(value) => setNewDamage({ ...newDamage, location: value })}
                >
                  <SelectTrigger className="bg-[#12151a] border-[#1a1d24]">
                    <SelectValue placeholder="Select Location" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12151a] border-[#1a1d24]">
                    {damageLocations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={newDamage.type}
                  onValueChange={(value) => setNewDamage({ ...newDamage, type: value })}
                >
                  <SelectTrigger className="bg-[#12151a] border-[#1a1d24]">
                    <SelectValue placeholder="Damage Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12151a] border-[#1a1d24]">
                    {damageTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={newDamage.severity}
                  onValueChange={(value) => setNewDamage({ ...newDamage, severity: value })}
                >
                  <SelectTrigger className="bg-[#12151a] border-[#1a1d24]">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12151a] border-[#1a1d24]">
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="severe">Severe</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Describe the damage..."
                  value={newDamage.description}
                  onChange={(e) => setNewDamage({ ...newDamage, description: e.target.value })}
                  className="bg-[#12151a] border-[#1a1d24]"
                />

                <Button
                  type="button"
                  onClick={addDamage}
                  variant="outline"
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Damage
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  placeholder="Any other observations..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-[#0a0c10] border-[#1a1d24]"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Signature */}
        {step === 4 && (
          <Card className="bg-[#12151a] border-[#1a1d24]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PenTool className="w-5 h-5 text-[#4ADE80]" />
                Signature & Confirmation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Signer Name *</Label>
                <Input
                  placeholder="Enter full name"
                  value={formData.signer_name}
                  onChange={(e) => setFormData({ ...formData, signer_name: e.target.value })}
                  className="bg-[#0a0c10] border-[#1a1d24]"
                />
              </div>

              <div className="space-y-2">
                <Label>Signer Role</Label>
                <Select
                  value={formData.signer_role}
                  onValueChange={(value) => setFormData({ ...formData, signer_role: value })}
                >
                  <SelectTrigger className="bg-[#0a0c10] border-[#1a1d24]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12151a] border-[#1a1d24]">
                    <SelectItem value="shipper">Shipper</SelectItem>
                    <SelectItem value="carrier">Carrier/Driver</SelectItem>
                    <SelectItem value="receiver">Receiver</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Signature *</Label>
                <SignaturePad
                  onSave={(data) => setFormData({ ...formData, signature_data: data })}
                  onClear={() => setFormData({ ...formData, signature_data: null })}
                />
                {formData.signature_data && (
                  <p className="text-xs text-green-400 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Signature captured
                  </p>
                )}
              </div>

              <div className="p-4 bg-[#0a0c10] rounded-lg">
                <p className="text-xs text-gray-400">
                  By signing, I confirm that this inspection report accurately represents 
                  the condition of the vehicle at the time of {inspectionType}.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
          )}
          
          {step < totalSteps ? (
            <Button
              onClick={() => setStep(step + 1)}
              className="flex-1 bg-[#4ADE80] text-black hover:bg-[#22C55E]"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !formData.signature_data || !formData.signer_name}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Complete Inspection
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InspectionPage;
